import React, { useState } from 'react';
import { TABLET_PORTRAIT, DESKTOP, XX_LARGE } from 'src/client/constants/mediaQueries';
import styled from 'styled-components/macro';
import { BrandedPillButton, PillButton, TEXT_SHADOW_LARGE, TEXT_SHADOW_SMALL } from './styles';
import ReactSlick, { Settings } from 'react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { StaticSplashResponse } from 'src/@types/StaticSplashResponse';
import { FreeGalleryEmail } from './FreeGalleryEmail';
import { useMediaUrl } from './useMediaUrls';

type Props = {
  freeGalleries?: StaticSplashResponse['freeGalleries'];
};

const nextArrow = (
  <PillButton>
    <FontAwesomeIcon icon={faChevronRight} />
  </PillButton>
);

const prevArrow = (
  <PillButton>
    <FontAwesomeIcon icon={faChevronLeft} />
  </PillButton>
);

const sliderSettings: Settings = {
  arrows: true,
  autoplay: false,
  autoplaySpeed: 5000,
  dots: true,
  fade: true,
  infinite: true,
  nextArrow,
  prevArrow,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 1000,
};

export const FreeGallerySlider = (props: Props) => {
  const { slides = [], buttonText = '' } = props.freeGalleries || {};

  const [showEmailSection, setShowEmailSection] = useState(false);
  const mediaUrl = useMediaUrl();

  const showSfwSite = useShowSfwSite();

  if (!props.freeGalleries || showSfwSite) {
    return null;
  }

  return (
    <Wrapper showEmailSection={showEmailSection}>
      <ReactSlick {...sliderSettings}>
        {slides.map((gallery) => (
          <Slide
            key={gallery.title}
            mobileBackgroundImage={`${mediaUrl}/free-gallery/${gallery.mobileImage}`}
            desktopBackgroundImage={`${mediaUrl}/free-gallery/${gallery.desktopImage}`}
          >
            <TextContainer hidden={showEmailSection}>
              <Title>{gallery.title}</Title>
              <Starring>
                Starring: <b>{gallery.modelName}</b>
              </Starring>
              <FreeGalleryButton onClick={() => setShowEmailSection(true)}>{buttonText}</FreeGalleryButton>
            </TextContainer>
          </Slide>
        ))}
      </ReactSlick>
      {showEmailSection && <FreeGalleryEmail setShowEmailSection={setShowEmailSection} />}
    </Wrapper>
  );
};

const Wrapper = styled.section<{ showEmailSection?: boolean }>`
  --aspect-ratio: 360 / 320;
  background-color: ${(props) => props.theme.headerBarPrimary5};
  grid-area: free-gallery;
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--aspect-ratio);

  @media ${TABLET_PORTRAIT} {
    --aspect-ratio: 768 / 246;
    align-items: flex-start;
    order: 2;
  }

  @media ${DESKTOP} {
    --aspect-ratio: 2560 / 720;
  }

  .slick-slider {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .slick-arrow {
    z-index: 10;
    margin: 0 5px;
    font-size: 28px;
    cursor: pointer;
    opacity: 1;
    background: none !important;

    // !important because the slider puts display block on the element in the dom
    display: ${(props) => (props.showEmailSection ? 'none' : 'flex')} !important;

    &:hover {
      opacity: 0.8;
      background: none !important;
    }

    @media ${TABLET_PORTRAIT} {
      font-size: 32px;
    }

    @media ${DESKTOP} {
      font-size: 48px;
    }

    @media ${XX_LARGE} {
      font-size: 64px;
    }

    svg {
      filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.75));
    }
  }

  .slick-prev {
    left: 0;
  }

  .slick-next {
    right: 0;
  }

  .slick-dots {
    padding: 0;
    margin: 0 auto;
    position: absolute;
    align-self: flex-end;
    display: flex !important;
    flex-direction: row;
    gap: 20px;
    list-style: none;
    width: 100%;
    justify-content: center;
    bottom: 25px;

    button {
      background: #fff;
      border-radius: 50%;
      border: 0;
      color: #fff;
      display: block;
      height: 10px;
      opacity: 0.5;
      outline: none;
      overflow: hidden;
      padding: 0;
      width: 10px;
    }

    .slick-active button {
      opacity: 1;
    }

    @media ${TABLET_PORTRAIT} {
      bottom: 20px;
    }

    @media ${DESKTOP} {
      bottom: 40px;
      gap: 30px;

      button {
        height: 14px;
        width: 14px;
      }
    }
  }
`;

const Slide = styled.div<{ mobileBackgroundImage: string; desktopBackgroundImage: string }>`
  display: inline-flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  aspect-ratio: var(--aspect-ratio);
  background-image: url(${(props) => props.mobileBackgroundImage});

  @media ${TABLET_PORTRAIT} {
    align-items: flex-start;
    background-image: url(${(props) => props.desktopBackgroundImage});
  }
`;

const TextContainer = styled.div<{ hidden: boolean }>`
  display: ${(props) => (props.hidden ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media ${TABLET_PORTRAIT} {
    width: 50%;
  }
`;

const Title = styled.span`
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  text-align: center;
  ${TEXT_SHADOW_LARGE};

  @media ${TABLET_PORTRAIT} {
    font-size: 28px;
  }

  @media ${DESKTOP} {
    font-size: 52px;
  }
`;

const Starring = styled.span`
  font-size: 14px;
  color: #fff;
  text-align: center;
  margin-bottom: 10px;
  ${TEXT_SHADOW_SMALL}

  b {
    font-weight: 600;
  }

  @media ${DESKTOP} {
    font-size: 18px;
  }
`;

export const FreeGalleryButton = styled(BrandedPillButton)`
  --height: 48px;
  font-size: 18px;

  @media ${TABLET_PORTRAIT} {
    --height: 36px;
    font-size: 14px;
  }

  @media ${DESKTOP} {
    --height: 68px;
    font-size: 24px;
  }
`;
